/**
 * Schema rules interpreter for questionnaire V2.1.
 * Pure functions: no UI, no side effects. Used to compute visibility,
 * required fields, conflicts, and payload normalization from questionnaireV2Schema.
 */
import { normalizeBucket } from "./parkKeys";
import {
  canonicalizeRidesBucket,
  canonicalizeShowsBucket,
  canonicalizeRestaurantsBucket,
} from "./canonicalize";

/**
 * Get value at dot path in nested form data (e.g. "parkTicketAccess.hasParkHopper").
 * @param {Object} formData - Nested form data { sectionId: { fieldId: value } }
 * @param {string} path - Dot-separated path
 * @returns {*} Value or undefined
 */
export function getValueAtPath(formData, path) {
  if (!formData || !path || typeof path !== "string") return undefined;
  const parts = path.split(".");
  let value = formData;
  for (const part of parts) {
    value = value?.[part];
    if (value === undefined) return undefined;
  }
  return value;
}

/**
 * Set value at dot path; returns new object (immutable).
 */
function setValueAtPath(formData, path, value) {
  if (!path || typeof path !== "string") return formData;
  const parts = path.split(".");
  if (parts.length === 0) return formData;
  if (parts.length === 1) return { ...formData, [parts[0]]: value };
  const [first, ...rest] = parts;
  const child = formData[first];
  const nextChild = setValueAtPath(
    child != null && typeof child === "object" ? child : {},
    rest.join("."),
    value
  );
  return { ...formData, [first]: nextChild };
}

export function evaluateCondition(formData, condition) {
  const { path, op, value: expected } = condition;
  const actual = getValueAtPath(formData, path);

  switch (op) {
    case "equals":
      return actual === expected;
    case "notEquals":
      return actual !== expected;
    case "includes":
      return Array.isArray(actual) && actual.includes(expected);
    case "notIncludes":
      return !Array.isArray(actual) || !actual.includes(expected);
    case "exists":
      return actual !== undefined && actual !== null && actual !== "";
    case "lengthGte":
      return Array.isArray(actual) && actual.length >= (expected ?? 0);
    case "gt":
      return typeof actual === "number" && typeof expected === "number" && actual > expected;
    case "gte":
      return typeof actual === "number" && typeof expected === "number" && actual >= expected;
    case "lt":
      return typeof actual === "number" && typeof expected === "number" && actual < expected;
    case "lte":
      return typeof actual === "number" && typeof expected === "number" && actual <= expected;
    default:
      return false;
  }
}

function whenAllMatch(formData, when) {
  if (!Array.isArray(when) || when.length === 0) return true;
  return when.every((cond) => evaluateCondition(formData, cond));
}

export function evaluateVisibility(schema, formData) {
  const visibleSectionIds = new Set();
  const visibleFieldIdsBySection = {};

  if (!schema?.sections) {
    return { visibleSectionIds: [], visibleFieldIdsBySection: {} };
  }

  const ruleByPath = new Map();

  for (const section of schema.sections) {
    visibleSectionIds.add(section.id);
    visibleFieldIdsBySection[section.id] = [];
    const fieldIds = section.fieldIds || (section.fields && section.fields.map((f) => f.id)) || [];
    for (const fid of fieldIds) {
      const path = `${section.id}.${fid}`;
      if (!visibleFieldIdsBySection[section.id]) visibleFieldIdsBySection[section.id] = [];
    }
  }

  for (const rule of schema.rules?.visibility || []) {
    const when = rule.when;
    const showPaths = rule.then?.show || [];
    for (const path of showPaths) {
      if (!ruleByPath.has(path)) ruleByPath.set(path, { when, then: rule.then });
    }
  }

  for (const section of schema.sections) {
    const fieldIds = section.fieldIds || (section.fields && section.fields.map((f) => f.id)) || [];
    const visible = [];
    for (const fid of fieldIds) {
      const path = `${section.id}.${fid}`;
      const rule = ruleByPath.get(path);
      if (!rule) {
        visible.push(fid);
      } else if (whenAllMatch(formData, rule.when)) {
        visible.push(fid);
      }
    }
    visibleFieldIdsBySection[section.id] = visible;
  }

  return {
    visibleSectionIds: Array.from(visibleSectionIds),
    visibleFieldIdsBySection,
  };
}

export function evaluateRequired(schema, formData) {
  const requiredFieldIdsBySection = {};

  if (!schema?.sections) return { requiredFieldIdsBySection: {} };

  for (const section of schema.sections) {
    requiredFieldIdsBySection[section.id] = [];
  }

  for (const path of schema.rules?.required || []) {
    const [sectionId, fieldId] = path.split(".");
    if (sectionId && fieldId && requiredFieldIdsBySection[sectionId]) {
      if (!requiredFieldIdsBySection[sectionId].includes(fieldId)) {
        requiredFieldIdsBySection[sectionId].push(fieldId);
      }
    }
  }

  for (const rule of schema.rules?.requiredIf || []) {
    const when = rule.when;
    const requiredPaths = rule.then?.required || [];
    if (!whenAllMatch(formData, when)) continue;
    for (const path of requiredPaths) {
      const [sectionId, fieldId] = path.split(".");
      if (sectionId && fieldId && requiredFieldIdsBySection[sectionId]) {
        if (!requiredFieldIdsBySection[sectionId].includes(fieldId)) {
          requiredFieldIdsBySection[sectionId].push(fieldId);
        }
      }
    }
  }

  return { requiredFieldIdsBySection };
}

export function detectConflicts(schema, formData) {
  const out = [];

  for (const conflict of schema.rules?.conflicts || []) {
    const { code, message, fieldIds } = conflict;
    const paths = fieldIds || [];
    for (const path of paths) {
      const value = getValueAtPath(formData, path);
      const isMulti = Array.isArray(value) && value.length > 1;
      if (isMulti) {
        out.push({ code, message, fieldIds: paths });
        break;
      }
    }
  }

  return out;
}

export function normalizePayload(schema, formData) {
  if (!formData || typeof formData !== "object") return formData;
  let data = JSON.parse(JSON.stringify(formData));

  for (const norm of schema.rules?.normalization || []) {
    const { sourcePath, targetPath, transform } = norm;
    const value = getValueAtPath(data, sourcePath);

    if (transform === "singleChoiceFromArray") {
      let single;
      if (Array.isArray(value) && value.length > 0) {
        single = value[0];
      } else if (typeof value === "string" && value) {
        single = value;
      } else {
        single = "";
      }
      data = setValueAtPath(data, targetPath, single);
    }
  }

  const arrayPaths = [
    "groupInfo.agesOfBoys",
    "groupInfo.agesOfGirls",
    "parkVisitPlans.parks",
    "attractionRideStrategy.mustRides",
    "showFireworkStrategy.mustSeeShows",
    "diningPreferences.mustTryRestaurants",
    "diningPreferences.diningTime",
    "diningPreferences.allergies",
    "timeEnergy.parkStartTime",
    "specialtyExperiences.mustExperience",
  ];
  for (const path of arrayPaths) {
    const v = getValueAtPath(data, path);
    if (v != null && !Array.isArray(v)) {
      data = setValueAtPath(data, path, []);
    }
    if (getValueAtPath(data, path) === undefined) {
      data = setValueAtPath(data, path, []);
    }
  }

  const ridesMust = getValueAtPath(data, "attractionRideStrategy.mustRidesByPark");
  const ridesNice = getValueAtPath(data, "attractionRideStrategy.niceRidesByPark");
  data = setValueAtPath(data, "attractionRideStrategy.mustRidesByPark", canonicalizeRidesBucket(normalizeBucket(ridesMust)));
  data = setValueAtPath(data, "attractionRideStrategy.niceRidesByPark", canonicalizeRidesBucket(normalizeBucket(ridesNice)));

  const showsMust = getValueAtPath(data, "showFireworkStrategy.mustSeeShowsByPark");
  const showsNice = getValueAtPath(data, "showFireworkStrategy.niceShowsByPark");
  data = setValueAtPath(data, "showFireworkStrategy.mustSeeShowsByPark", canonicalizeShowsBucket(normalizeBucket(showsMust)));
  data = setValueAtPath(data, "showFireworkStrategy.niceShowsByPark", canonicalizeShowsBucket(normalizeBucket(showsNice)));

  const restMust = getValueAtPath(data, "diningPreferences.mustTryRestaurantsByPark");
  const restNice = getValueAtPath(data, "diningPreferences.niceRestaurantsByPark");
  data = setValueAtPath(data, "diningPreferences.mustTryRestaurantsByPark", canonicalizeRestaurantsBucket(normalizeBucket(restMust)));
  data = setValueAtPath(data, "diningPreferences.niceRestaurantsByPark", canonicalizeRestaurantsBucket(normalizeBucket(restNice)));

  return data;
}
